import { customElement, scss } from '../../core/ids-decorators';
import { attributes } from '../../core/ids-attributes';
import IdsElement from '../../core/ids-element';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';

import styles from './ids-button-group.scss';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import { BUTTON_GROUP_FLEX_OPTIONS } from './ids-button-common';

const Base = IdsLocaleMixin(
  IdsEventsMixin(
    IdsElement
  )
);

/**
 * IDS Button Group Component
 * @type {IdsButtonGroup}
 * @inherits IdsElement
 * @mixes IdsEventsMixin
 * @mixes IdsLocaleMixin
 */
@customElement('ids-button-group')
@scss(styles)
export default class IdsButtonGroup extends Base {
  constructor() {
    super();
  }

  /**
   * Invoked each time the custom element is appended into a document-connected element.
   */
  connectedCallback() {
    super.connectedCallback();
    this.#attachEventHandlers();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes() {
    return [
      ...super.attributes,
      attributes.COMPACT,
      attributes.DISPLAY,
      attributes.DIRECTION,
      attributes.DIVIDER,
      attributes.JUSTIFY_CONTENT
    ];
  }

  /**
   * Sets up event listeners
   * @private
   * @returns {void}
   */
  #attachEventHandlers() {}

  /**
   * Create the Template for the contents
   * @returns {string} The template
   */
  template(): string {
    const template = `<div class="ids-button-group" part="button-group" 
      ${this.compact ? `compact="${this.compact}"` : ''}
      ${this.display ? `display="${this.display}"` : ''}
      ${this.direction ? `direction="${this.direction}"` : ''}
      ${this.divider ? `divider="${this.divider}"` : ''}
      ${this.justifyContent ? `justify-content="${this.justifyContent}"` : ''}
      >
      <slot></slot>
    </div>`;

    return template;
  }

  /**
   * Set the divider to button group
   * @param {string} value  The value of divider
   */
  set divider(value: string) {
    this.setAttribute(attributes.DIVIDER, value);
  }

  get divider(): boolean {
    return stringToBool(this.getAttribute(attributes.DIVIDER));
  }

  /**
   * Set the display setting
   * @param {string} value The value
   */
  set display(value: string | null) {
    if (value && BUTTON_GROUP_FLEX_OPTIONS.display.includes(value)) {
      this.setAttribute(attributes.DISPLAY, value);
    } else {
      this.removeAttribute(attributes.DISPLAY);
    }
  }

  get display() { return this.getAttribute(attributes.DISPLAY); }

  /**
   * Set the direction setting
   * @param {string} value The value
   */
  set direction(value: string | null) {
    if (value && BUTTON_GROUP_FLEX_OPTIONS.direction.includes(value)) {
      this.setAttribute(attributes.DIRECTION, value);
    } else {
      this.removeAttribute(attributes.DIRECTION);
    }
  }

  get direction() { return this.getAttribute(attributes.DIRECTION); }

  /**
   * Set the justify content setting
   * @param {string} value The value
   */
  set justifyContent(value: string | null) {
    if (value && BUTTON_GROUP_FLEX_OPTIONS.justifyContent.includes(value)) {
      this.setAttribute(attributes.JUSTIFY_CONTENT, value);
    } else {
      this.removeAttribute(attributes.JUSTIFY_CONTENT);
    }
  }

  get justifyContent() { return this.getAttribute(attributes.JUSTIFY_CONTENT); }

  /**
   * Set the compact to button group
   * @param {string} value  The value of divider
   */
  set compact(value: string) {
    this.setAttribute(attributes.COMPACT, value);
  }

  get compact(): boolean {
    return stringToBool(this.getAttribute(attributes.COMPACT));
  }
}
